import React from 'react';
import { DialogBox, DialogBoxBody, ButtonPrimary, Button } from '@americanexpress/dls-react';
import { DLSSize } from 'src/types/dls-size.enum';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import IconButton from 'src/components/icon-button/IconButton';
import { ButtonType } from 'src/types/button-type.enum';
import { IconClose } from '@americanexpress/dls-icons';
import Selectors from 'src/utils/selectors';
import styles from './dashboardModal.module.scss';
import colors from 'src/styles/colors.module.scss';
import AnnounceImage from 'src/assets/svg/dashboardModal/announcement.svg';
import StarImage from 'src/assets/svg/dashboardModal/star.svg';
import { DashboardModalImage } from 'src/types/dashboardModal.model';

interface DashboardModalListItemProps {
    title: string;
    content: JSX.Element;
}

function DashboardModalListItem({
    title,
    content,
    isMinWidthSmall
}: DashboardModalListItemProps & { isMinWidthSmall: boolean }): JSX.Element {
    return (
        <>
            <Typography
                variant={isMinWidthSmall ? TypographyVariants.BODY_BOLD : TypographyVariants.SUBTEXT_BOLD}
                data-cy={Selectors.SpendSmart.DASHBOARDMODAL_LISTITEM_TITLE}
            >
                {title}
            </Typography>
            <Typography
                variant={isMinWidthSmall ? TypographyVariants.BODY : TypographyVariants.SUBTEXT}
                className="pad-0"
                data-cy={Selectors.SpendSmart.DASHBOARDMODAL_LISTITEM_CONTENT}
            >
                {content}
            </Typography>
        </>
    );
}
export interface DashboardModalProps {
    id: string;
    title: string;
    img: DashboardModalImage;
    nextButtonText: string;
    skipButtonText: string;
    listItems: DashboardModalListItemProps[];
    handleClose: () => Promise<void>;
    handleSkip: () => Promise<void>;
    handleNext: () => Promise<void>;
    isMinWidthSmall: boolean;
    className?: string;
}

export function getSvgImage(img: DashboardModalImage): string {
    switch (img) {
        case DashboardModalImage.ANNOUNCEMENT:
            return AnnounceImage;
        // Add future image cases here
        case DashboardModalImage.NONE:
            return '';
        default:
            return '';
    }
}

export default function DashboardModal({
    title,
    img,
    nextButtonText,
    skipButtonText,
    listItems,
    id,
    handleClose,
    handleSkip,
    handleNext,
    isMinWidthSmall,
    className = ''
}: DashboardModalProps): JSX.Element {
    return (
        <DialogBox
            id={id}
            className={`${className} ${styles.dialogBox}`}
            aria-label={title}
            onClose={handleClose}
            data-cy={Selectors.SpendSmart.DASHBOARDMODAL}
        >
            <IconButton
                variant={ButtonType.PRIMARY_DARK}
                icon={<IconClose size={DLSSize.MEDIUM} />}
                title="close"
                onClick={handleClose}
                className={styles.closeButton}
                data-cy={Selectors.SpendSmart.CLOSE_BUTTON}
                role="button"
            />
            {img !== DashboardModalImage.NONE && (
                <div className={styles.dashboardImageContainer}>
                    <img
                        src={getSvgImage(img)}
                        alt="Dashboard image"
                        data-cy={Selectors.SpendSmart.DASHBOARDMODAL_IMAGE}
                        aria-hidden={true}
                    />
                </div>
            )}

            <Typography
                variant={isMinWidthSmall ? TypographyVariants.HEADING_S : TypographyVariants.HEADING_XS}
                className={`${styles.title} ${colors.brand}`}
                component="h2"
                data-cy={Selectors.SpendSmart.DASHBOARDMODAL_TITLE}
            >
                {title}
            </Typography>

            <DialogBoxBody id={`${id}-content`}>
                <div className={styles.contentContainer}>
                    {listItems.map((item, index) => (
                        <div
                            key={index}
                            className={styles.listItem}
                            data-cy={Selectors.SpendSmart.DASHBOARDMODAL_LISTITEM}
                        >
                            <img alt="Star bullet" src={StarImage} aria-hidden={true} />
                            <div className={styles.listItemContent}>
                                <DashboardModalListItem
                                    title={item.title}
                                    content={item.content}
                                    isMinWidthSmall={isMinWidthSmall}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </DialogBoxBody>

            <div className={styles.buttonContainer}>
                <ButtonPrimary
                    size={DLSSize.SMALL}
                    className={styles.button}
                    onClick={handleNext}
                    data-cy={Selectors.SpendSmart.NEXT_BUTTON}
                >
                    {nextButtonText}
                </ButtonPrimary>
                <Button
                    size={DLSSize.SMALL}
                    className={`${styles.button} ${colors.brand}`}
                    onClick={handleSkip}
                    data-cy={Selectors.SpendSmart.SKIP_BUTTON}
                >
                    {skipButtonText}
                </Button>
            </div>
        </DialogBox>
    );
}
